import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  OnInit,
  OnDestroy,
  HostListener
} from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Directive({
  selector: '[textStyle]',
  standalone: false,
})
export class TextStyleDirective implements OnChanges, OnInit, OnDestroy {
  @Input('textStyle') tokens: TokenNames | TokenNames[] = [];

  @Input() font?: TokenNames;
  @Input() color?: TokenNames;
  @Input() size?: TokenNames;
  @Input() spacing?: TokenNames;
  @Input() bgColor?: TokenNames;
  @Input() weight?: TokenNames;

  constructor(private el: ElementRef, private renderer: Renderer2) {
  }

  private resizeSub?: Subscription;

  ngOnChanges(_: SimpleChanges): void {
    this.applyStyles();
  }

  ngOnInit(): void {
    // Recalcula estilos quando o tamanho da janela mudar (tokens responsivos)
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(150))
      .subscribe(() => this.applyStyles());
  }

  ngOnDestroy(): void {
    this.resizeSub?.unsubscribe();
  }

  private applyStyles(): void {
    const element = this.el.nativeElement;
    const rootStyles = window.getComputedStyle(document.documentElement);
    const styleObj: { [key: string]: string } = {};

    const tokenList = Array.isArray(this.tokens)
      ? this.tokens
      : this.tokens?.split(' ').filter(Boolean) ?? [];

    const regex = /(\d{3})\s+([\d.]+px)\/([\d.]+px)\s+(?:"([^"]+)"|'([^']+)')/;

    // Tokens combinados
    tokenList.forEach(token => {
      const tokenValue = rootStyles.getPropertyValue(`--${token.trim()}`).trim();
      const match = tokenValue.match(regex);
      if (match) {
        const [, weight, size, lineHeight, font1, font2] = match;
        const fontFamily = font1 || font2;
        Object.assign(styleObj, {
          'font-weight': weight,
          'font-size': size,
          'line-height': lineHeight,
          'font-family': fontFamily,
        });
      }
    });

    // Inputs isolados (também via tokens)
    const resolveTokenValue = (tokenName?: string): string | undefined => {
      if (!tokenName) return;
      return rootStyles.getPropertyValue(`--${tokenName.trim()}`).trim() || tokenName;
    };

    const fontResolved = resolveTokenValue(this.font);
    if (fontResolved) {
      // Tenta aplicar parsing como se fosse shorthand de fonte
      const match = fontResolved.match(regex);
      if (match) {
        const [, weight, size, lineHeight, font1, font2] = match;
        styleObj['font-weight'] = weight;
        styleObj['font-size'] = size;
        styleObj['line-height'] = lineHeight;
        styleObj['font-family'] = font1 || font2;
      } else {
        styleObj['font-family'] = fontResolved;
      }
    }

    const colorResolved = resolveTokenValue(this.color);
    if (colorResolved) styleObj['color'] = colorResolved;

    const sizeResolved = resolveTokenValue(this.size);
    if (sizeResolved) styleObj['font-size'] = sizeResolved;

    const weightResolved = resolveTokenValue(this.weight);
    if (weightResolved) styleObj['font-weight'] = weightResolved;

    const spacingResolved = resolveTokenValue(this.spacing);
    if (spacingResolved) styleObj['margin-bottom'] = spacingResolved;

    const bgColorResolved = resolveTokenValue(this.bgColor);
    if (bgColorResolved) styleObj['background-color'] = bgColorResolved;

    // Aplica os estilos no elemento
    Object.entries(styleObj).forEach(([prop, val]) => {
      this.renderer.setStyle(element, prop, val);
    });
  }
}


type TokenNames =
  'mar-primary-50' |
  'mar-primary-100' |
  'mar-primary-200' |
  'mar-primary-300' |
  'mar-primary-400' |
  'mar-primary-500' |
  'mar-primary-600' |
  'mar-primary-700' |
  'mar-primary-800' |
  'mar-primary-900' |
  'mar-primary-950' |
  'mar-greyscale-0' |
  'mar-greyscale-50' |
  'mar-greyscale-100' |
  'mar-greyscale-200' |
  'mar-greyscale-300' |
  'mar-greyscale-400' |
  'mar-greyscale-500' |
  'mar-greyscale-600' |
  'mar-greyscale-700' |
  'mar-greyscale-800' |
  'mar-greyscale-900' |
  'mar-greyscale-950' |
  'mar-greyscale-1000' |
  'mar-support-red-50' |
  'mar-support-red-100' |
  'mar-support-red-200' |
  'mar-support-red-300' |
  'mar-support-red-400' |
  'mar-support-red-500' |
  'mar-support-red-600' |
  'mar-support-red-700' |
  'mar-support-red-800' |
  'mar-support-red-900' |
  'mar-support-red-950' |
  'mar-support-yellow-50' |
  'mar-support-yellow-100' |
  'mar-support-yellow-200' |
  'mar-support-yellow-300' |
  'mar-support-yellow-400' |
  'mar-support-yellow-500' |
  'mar-support-yellow-600' |
  'mar-support-yellow-700' |
  'mar-support-yellow-800' |
  'mar-support-yellow-900' |
  'mar-support-yellow-950' |
  'mar-support-green-50' |
  'mar-support-green-100' |
  'mar-support-green-200' |
  'mar-support-green-300' |
  'mar-support-green-400' |
  'mar-support-green-500' |
  'mar-support-green-600' |
  'mar-support-green-700' |
  'mar-support-green-800' |
  'mar-support-green-900' |
  'mar-support-green-950' |
  'mar-support-blue-50' |
  'mar-support-blue-100' |
  'mar-support-blue-200' |
  'mar-support-blue-300' |
  'mar-support-blue-400' |
  'mar-support-blue-500' |
  'mar-support-blue-600' |
  'mar-support-blue-700' |
  'mar-support-blue-800' |
  'mar-support-blue-900' |
  'mar-support-blue-950' |
  'mar-font-size-12px' |
  'mar-font-size-14px' |
  'mar-font-size-16px' |
  'mar-font-size-18px' |
  'mar-font-size-20px' |
  'mar-font-size-22px' |
  'mar-font-size-24px' |
  'mar-font-size-26px' |
  'mar-font-size-28px' |
  'mar-font-size-30px' |
  'mar-font-size-32px' |
  'mar-font-size-36px' |
  'mar-font-size-38px' |
  'mar-font-size-40px' |
  'mar-font-size-42px' |
  'mar-font-size-44px' |
  'mar-font-size-46px' |
  'mar-font-size-48px' |
  'mar-font-size-50px' |
  'mar-font-size-52px' |
  'mar-font-size-54px' |
  'mar-font-size-56px' |
  'mar-font-size-58px' |
  'mar-font-size-60px' |
  'mar-text-case-uppercase' |
  'mar-text-case-lowercase' |
  'mar-text-case-capitalize' |
  'mar-text-case-none' |
  'mar-text-decoration-none' |
  'mar-text-decoration-underline' |
  'mar-text-decoration-line-through' |
  'mar-font-family-open-sans' |
  'mar-line-height-16px' |
  'mar-line-height-20px' |
  'mar-line-height-24px' |
  'mar-line-height-28px' |
  'mar-line-height-32px' |
  'mar-line-height-36px' |
  'mar-line-height-40px' |
  'mar-line-height-44px' |
  'mar-line-height-48px' |
  'mar-line-height-52px' |
  'mar-line-height-56px' |
  'mar-line-height-60px' |
  'mar-line-height-64px' |
  'mar-line-height-68px' |
  'mar-letter-spacing-0' |
  'mar-font-weight-400' |
  'mar-font-weight-500' |
  'mar-font-weight-600' |
  'mar-font-weight-700' |
  'mar-spacing-0' |
  'mar-spacing-50' |
  'mar-spacing-100' |
  'mar-spacing-150' |
  'mar-spacing-200' |
  'mar-spacing-250' |
  'mar-spacing-300' |
  'mar-spacing-350' |
  'mar-spacing-400' |
  'mar-spacing-450' |
  'mar-spacing-500' |
  'mar-spacing-550' |
  'mar-spacing-600' |
  'mar-spacing-650' |
  'mar-spacing-700' |
  'mar-spacing-750' |
  'mar-spacing-800' |
  'mar-spacing-850' |
  'mar-spacing-900' |
  'mar-spacing-950' |
  'mar-spacing-1000' |
  'mar-spacing-default-xs' |
  'mar-spacing-default-sm' |
  'mar-spacing-default-md' |
  'mar-spacing-default-lg' |
  'mar-spacing-default-xl' |
  'mar-spacing-default-2xl' |
  'mar-spacing-default-3xl' |
  'mar-spacing-default-4xl' |
  'mar-spacing-default-component-fixed-xs' |
  'mar-spacing-default-component-fixed-sm' |
  'mar-spacing-default-component-fixed-md' |
  'mar-spacing-default-component-fixed-lg' |
  'mar-spacing-default-component-fixed-xl' |
  'mar-spacing-default-component-responsive-xs' |
  'mar-spacing-default-component-responsive-sm' |
  'mar-border-width-none' |
  'mar-border-width-thin' |
  'mar-border-width-regular' |
  'mar-border-radius-none' |
  'mar-border-radius-xs' |
  'mar-border-radius-sm' |
  'mar-border-radius-md' |
  'mar-border-radius-lg' |
  'mar-border-radius-xl' |
  'mar-border-radius-2xl' |
  'mar-border-radius-circular' |
  'mar-sizing-0' |
  'mar-sizing-50' |
  'mar-sizing-100' |
  'mar-sizing-150' |
  'mar-sizing-200' |
  'mar-sizing-250' |
  'mar-sizing-300' |
  'mar-sizing-350' |
  'mar-sizing-400' |
  'mar-sizing-450' |
  'mar-sizing-500' |
  'mar-sizing-550' |
  'mar-sizing-600' |
  'mar-sizing-650' |
  'mar-sizing-700' |
  'mar-sizing-750' |
  'mar-sizing-800' |
  'mar-sizing-850' |
  'mar-sizing-900' |
  'mar-sizing-950' |
  'mar-sizing-1000' |
  'mar-sizing-fixed-xs' |
  'mar-sizing-fixed-sm' |
  'mar-sizing-fixed-md' |
  'mar-sizing-fixed-lg' |
  'mar-sizing-fixed-xl' |
  'mar-sizing-fixed-2xl' |
  'mar-sizing-fixed-3xl' |
  'mar-sizing-fixed-4xl' |
  'mar-sizing-responsive-sm' |
  'mar-sizing-responsive-md' |
  'mar-background-primary' |
  'mar-background-secondary' |
  'mar-background-inverse' |
  'mar-background-brand' |
  'mar-surface-primary' |
  'mar-surface-secondary' |
  'mar-surface-inverse' |
  'mar-surface-brand' |
  'mar-foreground-primary-default' |
  'mar-foreground-primary-hover' |
  'mar-foreground-primary-active' |
  'mar-foreground-primary-disabled' |
  'mar-foreground-secondary-default' |
  'mar-foreground-secondary-hover' |
  'mar-foreground-secondary-active' |
  'mar-foreground-secondary-disabled' |
  'mar-foreground-tertiary-default' |
  'mar-foreground-tertiary-hover' |
  'mar-foreground-tertiary-active' |
  'mar-foreground-tertiary-disabled' |
  'mar-foreground-support-success' |
  'mar-foreground-support-warning' |
  'mar-foreground-support-danger' |
  'mar-foreground-support-info' |
  'mar-border-primary-default' |
  'mar-border-primary-hover' |
  'mar-border-primary-active' |
  'mar-border-primary-disabled' |
  'mar-border-secondary-default' |
  'mar-border-secondary-hover' |
  'mar-border-secondary-active' |
  'mar-border-secondary-disabled' |
  'mar-border-inverse-default' |
  'mar-border-inverse-hover' |
  'mar-border-inverse-active' |
  'mar-border-inverse-disabled' |
  'mar-border-support-success' |
  'mar-border-support-warning' |
  'mar-border-support-danger' |
  'mar-border-support-info' |
  'mar-border-focus-primary' |
  'mar-text-primary' |
  'mar-text-secondary' |
  'mar-text-tertiary' |
  'mar-text-inverse' |
  'mar-text-disabled' |
  'mar-text-support-success' |
  'mar-text-support-warning' |
  'mar-text-support-danger' |
  'mar-text-support-info' |
  'mar-overlays-default' |
  'mar-heading-xxl' |
  'mar-heading-xl' |
  'mar-heading-lg' |
  'mar-heading-md' |
  'mar-heading-sm' |
  'mar-body-lg' |
  'mar-body-md' |
  'mar-body-sm' |
  'mar-label-lg' |
  'mar-label-md' |
  'mar-label-sm' |
  'mar-caption-default' |
  'mar-caption-strong' |
  'mar-button-lg' |
  'mar-button-md' |
  'mar-button-sm' |
  'mar-links-lg' |
  'mar-links-md' |
  'mar-links-sm';
